<template>
  <div>
    <div
      ref="chart"
      :class="'js_export_report_dymanic_chart'"
      :style="showStub ? {pointerEvents: 'none'} : null"
    />
    <div
      :class="[$style.legend, 'export_html_legend']"
      :style="{width: width, left: leftPos}"
    >
      <div
        v-for="(item, index) in legendItems"
        :key="index"
        :class="[
          $style.wrapper,
          'export_html_legend_item',
          disabledSeries[item.name] && 'disabled'
        ]"
        @click="toggleSelect(item.name)"
      >
        <div
          v-if="item.type === 'bar'"
          :class="[$style.box, 'export_html_box']"
          :style="{background: item.color}"
        />
        <div
          v-if="item.type === 'line'"
          :class="[$style.icon, 'export_html_icon']"
          :style="{color: item.color}"
          v-html="svgLegendItem"
        />
        <div :class="[$style.name, 'export_html_name']">
          {{ item.label }}
        </div>
      </div>
    </div>
    <div
      v-if="showStub && showStubMessage"
      :class="$style.stub_description"
      :style="{width: width, left: leftPos}"
    >
      <div :class="$style.wrapper">
        <span :class="$style.text">
          {{ locales(`report_page_no_filtered_data`) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from '../../../../libs/echarts';

const svgLegendItem = `<svg width="34" height="14" viewBox="0 0 34 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g id="Frame 33235">
  <circle id="Ellipse 1114" cx="17" cy="7" r="6.5" fill="currentColor" stroke="currentColor"/>
  <line id="Line 8" x1="1.6756e-07" y1="7" x2="34" y2="7" stroke="currentColor" stroke-width="4"/>
  </g>
</svg>`;

export default {
  props: {
    isStatic: { type: Boolean, required: true, default: false },
    config: { type: Object, required: false, default: () => {} },
    stubConfig: { type: Object, required: true, default: () => {} },
    customLegends: { type: Boolean, required: false, default: false },
    showStubMessage: { type: Boolean, required: false, default: true },
  },
  emits: {
    toggleSeries: String,
    onAllLegendsDisabled: null,
  },
  data() {
    return {
      id: 'chart',
      locales: __,
      width: '100%',
      leftPos: 0,
      disabledSeries: {},
      legendItems: [],
      timeout: null,
      localConfig: null,
    };
  },
  computed: {
    svgLegendItem() {
      return svgLegendItem;
    },
    leftSidebarWidth() {
      return this.$store.getters.leftSidebarWidth;
    },
    allLegendItemsDisabled() {
      return this.legendItems.every(item => this.disabledSeries[item.name]);
    },
    showStub() {
      return this.isStatic || this.allLegendItemsDisabled;
    },
  },
  watch: {
    config: {
      handler(conf) {
        if (conf) {
          this.setLegendItems();
          if (!this.allLegendItemsDisabled) {
            setTimeout(() => {
              this.localConfig = conf;
              this.instance.setOption(this.localConfig);
            }, 0);
          }
        }
      },
      immediate: true,
    },
    isStatic: {
      handler(flag) {
        if (!this.instance) {
          return;
        }

        if (flag) {
          this.disableLegendsItems();
        }

        setTimeout(() => this.onResize(flag), 0);
      },
      immediate: true,
    },
    leftSidebarWidth() {
      this.onResize();
    },
    showStub(flag) {
      if (flag) {
        this.instance.setOption(this.stubConfig);
      } else {
        this.instance.setOption(this.config);
      }
      this.$emit('onAllLegendsDisabled', flag);
    },
  },
  mounted() {
    this.instance = echarts.init(this.$refs.chart);
    this.instance.setOption(this.stubConfig);
    this.setLegendItems();
    this.onResize();

    this.instance.on('legendselectall', () => {
      this.disabledSeries = {};
      this.setLegendItems();
    });

    window.addEventListener('resize', this.onResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    echarts.dispose(this.instance);
  },
  methods: {
    setLegendItems() {
      if (this.customLegends) {
        const series = this.isStatic ? this.stubConfig.series : this.config?.series;

        this.legendItems = series.map((item, index) => ({
          name: item.name,
          label: item.locale ? __(`${item.locale}`) : '-',
          color: this.disabledSeries[item.name] ? series[index].disabledColor : series[index].color,
          type: item.type,
        }));
      }
    },
    disableLegendsItems() {
      const stubSeries = this.stubConfig.series;

      this.legendItems = this.legendItems.map((item, index) => ({
        ...item,
        label: '-',
        color: stubSeries[index].color,
      }));
    },
    onResize() {
      const width = document.documentElement.clientWidth - this.leftSidebarWidth;
      const height = document.documentElement.clientHeight - 220;

      this.instance.resize({ width, height });
      this.width = `${width}px`;
      this.leftPos = `${this.leftSidebarWidth}px`;
    },
    toggleSelect(name) {
      if (this.isStatic) {
        return;
      }

      const activeSeriesCountBefore = this.legendItems.filter(item => !this.disabledSeries[item.name]).length;

      if (name in this.disabledSeries) {
        delete this.disabledSeries[name];
      } else {
        this.disabledSeries[name] = true;
      }

      this.setLegendItems();

      const activeSeriesCountAfter = this.legendItems.filter(item => !this.disabledSeries[item.name]).length;

      if (!activeSeriesCountAfter) {
        const updatedConfig = { ...this.stubConfig };

        updatedConfig.legend = {
          ...updatedConfig.legend,
          selected: this.legendItems.reduce((acc, item) => {
            acc[item.name] = true;

            return acc;
          }, {}),
        };
        this.instance.setOption(updatedConfig);
      } else if (!activeSeriesCountBefore) {
        const updatedConfig = { ...this.localConfig };

        updatedConfig.legend = {
          ...updatedConfig.legend,
          selected: this.legendItems.reduce((acc, item) => {
            acc[item.name] = !this.disabledSeries[item.name];

            return acc;
          }, {}),
        };
        this.instance.setOption(updatedConfig);
      } else {
        this.instance.dispatchAction({
          type: 'legendToggleSelect',
          name,
        });
      }

      this.$emit('toggleSeries', this.instance);
    },
  },
};
</script>
<style module lang="less">
@import '../../../../less/_mixins.less';
@import '../../../../less/_fonts.less';
.stub_description {
  display: flex;
  justify-content: center;
  position: fixed;
  top: 50%;
  transform: translateY(-50%);

  .wrapper {
    background: #fff;
    padding: 10px;

    .text {
      color: #191919;
      opacity: .5;
      .FontLatoRegular;
      font-size: 18px;
      text-align: center;
      white-space: nowrap;
    }
  }
}

.legend {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: 52px;
  left: 50%;

  .wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    .icon {
      display: flex;
      align-items: center;
      width: 20px;
      height: 12px;
    }

    .box {
      width: 20px;
      height: 12px;
      border-radius: 2px;
    }

    .name {
      .FontLatoRegular;
      font-size: 14px;
      color: #191919;
      padding-left: 8px;
      // width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &:not(:last-child) {
      margin-right: 24px;
    }
  }
}
</style>
